import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeller } from '../context/SellerContext';
import { getProductsByCategory, getSellerBySubdomain, getCategorySettings, getPublicBanners, getSellerBanners } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useCategories } from '../context/CategoryContext';
import { formatPrice } from '../utils/format';
import SellerHeader from '../components/SellerHeader';
import SellerFooter from '../components/SellerFooter';
import FloatingMenu from '../components/FloatingMenu';

function SellerShop({ cartCount, addToCart }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { subdomain, shopName, seller: initialSellerInfo } = useSeller();
  const { categories: ctxCategories, loading: categoriesLoading } = useCategories();
  
  const [sections, setSections] = useState([]); // 동적 섹션: [{ key, title, products }]
  const [loading, setLoading] = useState(true);
  const [sellerInfo, setSellerInfo] = useState(initialSellerInfo || {});
  const [sortedCategories, setSortedCategories] = useState([]);
  const [heroBanner, setHeroBanner] = useState(null);

  // Update seller info if initialSellerInfo changes or if we need to fetch independent details
  useEffect(() => {
    if (initialSellerInfo) {
      setSellerInfo(initialSellerInfo);
    } else if (subdomain) {
      // Fallback: fetch seller info if not provided by context (though context usually provides it)
      getSellerBySubdomain(subdomain).then(data => setSellerInfo(data)).catch(() => { /* 컨텍스트 폴백: 판매자 정보 미로드 시 빈 상태 유지 */ });
    }
  }, [initialSellerInfo, subdomain]);

  // Fetch Banners (Hero Section) - 판매자 배너 우선, 없으면 공용 배너 폴백
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        // 1차: 판매자 배너 조회
        if (subdomain) {
          const sellerRes = await getSellerBanners(subdomain);
          const sellerBanners = sellerRes.data;
          if (sellerBanners && sellerBanners.length > 0) {
            const active = sellerBanners.filter(b => b.active !== false);
            if (active.length > 0) {
              setHeroBanner(active[0]);
              return;
            }
          }
        }
        // 2차: 공용 배너 폴백
        const response = await getPublicBanners('main');
        const banners = response.data;
        if (banners && banners.length > 0) {
           const activeBanners = banners.filter(b => b.active !== false);
           if (activeBanners.length > 0) {
             setHeroBanner(activeBanners[0]);
           }
        }
      } catch (error) {
        // silently ignore
      }
    };
    fetchBanners();
  }, [subdomain]);

  // CategoryContext에서 활성 카테고리 (displayOrder 순)
  useEffect(() => {
    if (ctxCategories.length > 0) {
      const SPECIAL = [
        { name: '베스트 상품', value: 'best', displayOrder: -99 },
        { name: '신상품', value: 'new', displayOrder: -98 },
        { name: '추천 상품', value: 'recommended', displayOrder: -97 }
      ];

      const dbCats = ctxCategories.map(cat => ({
        name: cat.name || cat.title,
        value: cat.categoryValue,
        hot: cat.isHot || false,
        displayOrder: cat.displayOrder || 999,
      }));

      const hotCat = dbCats.find(c => c.hot);
      const regularCats = dbCats.filter(c => !c.hot);

      const combined = [
        ...(hotCat ? [{ ...hotCat, displayOrder: -100 }] : []),
        ...SPECIAL,
        ...regularCats,
      ];
      combined.sort((a, b) => a.displayOrder - b.displayOrder);
      setSortedCategories(combined);
    }
  }, [ctxCategories]);

  // Branding Defaults
  const displayName = sellerInfo?.shopName || shopName || "FlowerShop";

  // Determine Banner Content (Custom vs Default)
  // 배너 이미지가 있을 때만 배경 이미지 사용. 없으면 로고/외부 스톡 대신 테마 다크 패널로 폴백.
  const bannerData = heroBanner ? {
    title: heroBanner.title,
    description: heroBanner.description || "특별한 혜택을 만나보세요",
    imageUrl: heroBanner.imageUrl,
    hasImage: !!heroBanner.imageUrl,
    linkUrl: heroBanner.linkUrl,
    buttonText: heroBanner.buttonText || "자세히 보기",
    badgeText: heroBanner.badgeText || "SEASON PROMOTION",
    showButton: !!heroBanner.linkUrl
  } : {
    title: <>{displayName}<br/>추천 상품</>,
    description: `${displayName}이 준비한 상품을 만나보세요.`,
    imageUrl: null,
    hasImage: false,
    linkUrl: null, // Handled as default action
    buttonText: "베스트 상품 보러가기",
    badgeText: "SEASON PROMOTION",
    showButton: true
  };

  const handleBannerClick = () => {
    if (bannerData.linkUrl) {
      if (bannerData.linkUrl.startsWith('http')) {
        window.location.href = bannerData.linkUrl;
      } else {
        navigate(bannerData.linkUrl);
      }
    } else {
      // Default action (only triggers if showButton is true and link is null, which works for default banner)
      handleCategoryClick('best');
    }
  };

  // Design Assets for Categories
  const CATEGORY_ASSETS = {
    'celebration-wreath': { icon: 'celebration', color: 'text-primary-strong' },
    'condolence-wreath': { icon: 'sentiment_sad', color: 'text-gray-600 dark:text-gray-300' },
    'flower-basket': { icon: 'shopping_basket', color: 'text-pink-400' },
    'foliage-plant': { icon: 'potted_plant', color: 'text-primary-strong' },
    'eastern-orchid': { icon: 'eco', color: 'text-orange-400' },
    'western-orchid': { icon: 'local_florist', color: 'text-purple-400' },
    'flower-bouquet': { icon: 'redeem', color: 'text-red-400' },
    'best': { icon: 'thumb_up', color: 'text-blue-500' },
    'hotdeal': { icon: 'local_fire_department', color: 'text-red-500' },
    'new': { icon: 'fiber_new', color: 'text-green-500' },
    'recommended': { icon: 'recommend', color: 'text-yellow-500' },
  };

  // Compute Grid Categories (Top 5 from sorted list)
  const gridCategories = sortedCategories.slice(0, 5).map(cat => ({
    ...cat,
    ...(CATEGORY_ASSETS[cat.value] || { icon: 'category', color: 'text-gray-500' }) // Default Fallback
  }));

  // 동적 섹션: 하드코딩(축하/근조) 대신 가맹점이 실제 취급하는 카테고리 기반으로 구성.
  // 상품 있는 카테고리만 섹션으로 노출(빈 섹션 숨김), 카테고리 로딩 완료 후 빌드.
  useEffect(() => {
    if (!subdomain || categoriesLoading) return; // 카테고리 로딩 완료까지 대기
    let cancelled = false;
    const toArray = (data) => Array.isArray(data) ? data : (data?.content || []);
    const build = async () => {
      setLoading(true);
      const realCats = (ctxCategories || [])
        .map(c => ({ name: c.name || c.title, value: c.categoryValue, order: c.displayOrder ?? 999 }))
        .filter(c => c.value)
        .sort((a, b) => a.order - b.order)
        .slice(0, 6);
      // 카테고리별 상품을 병렬로 조회 (직렬 대기 제거 → 로딩 시간 단축, 한 번에 렌더)
      const fetched = await Promise.all(realCats.map(async (cat) => {
        try {
          const res = await getProductsByCategory(cat.value, subdomain);
          return { cat, items: toArray(res.data) };
        } catch (e) { return { cat, items: [] }; }
      }));
      const result = [];
      const seen = new Set();
      for (const { cat, items } of fetched) {
        if (result.length >= 4) break; // 최대 4개 섹션
        const uniq = items.filter(p => p && !seen.has(p.id)).slice(0, 8);
        if (uniq.length > 0) {
          uniq.forEach(p => seen.add(p.id));
          result.push({ key: cat.value, title: cat.name, products: uniq });
        }
      }
      if (!cancelled) {
        setSections(result);
        setLoading(false);
      }
    };
    build();
    return () => { cancelled = true; };
  }, [subdomain, ctxCategories, categoriesLoading]);


  const handleCategoryClick = (value) => {
    navigate(`/products?category=${value}`);
  };

  const handleProductClick = (id) => {
    navigate(`/products/${id}`);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (addToCart) {
      addToCart(product, 1, []);
      showToast('장바구니에 상품이 담겼습니다.', 'success');
    } else {
      showToast('장바구니 기능 오류입니다.', 'error');
    }
  };

  return (
    <div className="bg-background-light dark:bg-background-dark text-[#0d1b12] dark:text-[#f8fcf9] transition-colors duration-300 font-display">
      <style>{`
        .material-symbols-outlined {
          font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
        /* 상품 이미지: placeholder 배경 위에 로드 완료 시 페이드인 (더더덕 팝인 방지)
           transform 도 함께 선언해 hover scale 트랜지션 유지 */
        .fade-img { opacity: 0; transition: opacity 0.35s ease, transform 0.5s ease; }
        .fade-img.img-loaded { opacity: 1; }
      `}</style>
      <div className="relative flex min-h-screen flex-col overflow-x-hidden">
        <SellerHeader cartCount={cartCount} />

        <main className="mx-auto w-full max-w-[1200px] flex-1 pt-20">
          {/* Search Bar Section */}
          <section className="px-4 lg:px-10 py-6 lg:py-8">
            <div className="mx-auto max-w-[800px]">
              <label className="relative flex h-12 lg:h-14 w-full shadow-sm">
                <div
                  className="absolute inset-y-0 left-0 flex items-center pl-4 lg:pl-5 text-primary-strong"
                  style={{ color: 'var(--primary-strong)' }}
                >
                  <span className="material-symbols-outlined">search</span>
                </div>
                <input 
                  className="w-full rounded-2xl border-none bg-white dark:bg-[#1d3324] pl-12 lg:pl-14 pr-20 lg:pr-24 text-sm lg:text-base font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-primary dark:text-white transition-all shadow-lg" 
                  placeholder="원하시는 꽃이나 화환을 검색해보세요"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      navigate(`/products?search=${e.target.value}`);
                    }
                  }}
                />
                <button 
                  className="absolute right-2 top-2 bottom-2 px-4 lg:px-6 rounded-xl bg-primary text-primary-contrast font-bold text-xs lg:text-sm hover:bg-primary-hover transition-colors"
                  style={{ backgroundColor: 'var(--primary-color)' }}
                >
                  검색하기
                </button>
              </label>
            </div>
          </section>

          {/* Category Grid */}
          <section className="px-4 lg:px-10 py-4 lg:py-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 lg:gap-4">
              {gridCategories.map((cat) => (
                <div 
                  key={cat.value}
                  onClick={() => handleCategoryClick(cat.value)}
                  className="group flex flex-col items-center gap-3 rounded-2xl border bg-white dark:bg-[#162a1c] p-6 text-center cursor-pointer hover:border-primary hover:shadow-md transition-all"
                  style={{ borderColor: 'rgb(var(--primary-rgb) / 0.3)' }}
                >
                  <div 
                    className={`flex h-14 w-14 items-center justify-center rounded-full ${cat.color} group-hover:scale-110 transition-transform`}
                    style={{ 
                      backgroundColor: 'rgb(var(--primary-rgb) / 0.1)',
                      color: cat.color && cat.color.includes('text-primary') ? 'var(--primary-strong)' : undefined
                    }}
                  >
                    <span className="material-symbols-outlined text-3xl">{cat.icon}</span>
                  </div>
                  <h2 className="text-sm font-bold dark:text-white">{cat.name}</h2>
                </div>
              ))}
            </div>
          </section>

          {/* Main Promo Banner */}
          <section className="px-4 lg:px-10 py-4 lg:py-6">
            <div
              className={`relative overflow-hidden rounded-3xl min-h-[300px] lg:min-h-[400px] flex items-center transition-all duration-500 group ${bannerData.hasImage ? 'bg-cover bg-center bg-no-repeat' : ''}`}
              style={bannerData.hasImage ? { backgroundImage: `url('${bannerData.imageUrl}')` } : { backgroundColor: '#0d1b12' }}
            >
              {/* 이미지 배너일 때만 가독성용 어둠 오버레이 */}
              {bannerData.hasImage && (
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-500" />
              )}

              <div className="relative z-10 w-full p-6 lg:p-16 text-white space-y-4 lg:space-y-6">
                <div
                  className="inline-block px-3 py-1 lg:px-4 lg:py-1 rounded-full bg-secondary text-secondary-contrast text-[10px] lg:text-xs font-bold mb-2"
                  style={{ backgroundColor: 'var(--secondary-color)' }}
                >
                  {bannerData.badgeText}
                </div>
                <h3 className="text-2xl lg:text-6xl font-extrabold leading-tight drop-shadow-lg max-w-3xl">
                  {bannerData.title}
                </h3>
                <p className="text-sm lg:text-lg text-gray-100 max-w-2xl drop-shadow-md font-medium">
                  {bannerData.description}
                </p>
                {bannerData.showButton && (
                  <button 
                    onClick={handleBannerClick}
                    className="mt-2 lg:mt-4 bg-white text-background-dark px-6 py-3 lg:px-10 lg:py-4 rounded-xl font-bold text-sm lg:text-lg hover:bg-primary hover:text-primary-contrast hover:scale-105 transition-all shadow-lg"
                  >
                    {bannerData.buttonText}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Dynamic Sections — 가맹점 취급 카테고리 기반 (하드코딩 제거) */}
          {loading ? (
            <section className="px-4 lg:px-10 py-6 lg:py-10">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
                    <div className="aspect-[3/4] bg-gray-100 dark:bg-gray-800 animate-pulse" />
                    <div className="p-3 lg:p-5 space-y-2">
                      <div className="h-4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                      <div className="h-5 w-1/2 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : sections.length === 0 ? (
            <section className="px-4 lg:px-10 py-16 text-center text-gray-500">
              <span className="material-symbols-outlined text-5xl mb-3 block" style={{ color: 'var(--primary-strong)' }}>local_florist</span>
              <p className="font-bold text-gray-700 dark:text-gray-300 mb-1">상품을 준비 중입니다</p>
              <p className="text-sm mb-5">곧 멋진 상품으로 찾아뵙겠습니다.</p>
              <button
                onClick={() => navigate('/products')}
                className="px-6 py-3 rounded-xl font-bold text-sm text-primary-contrast"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                전체 상품 보기
              </button>
            </section>
          ) : (
            sections.map((sec) => (
              <section key={sec.key} className="px-4 lg:px-10 py-6 lg:py-10">
                <div className="flex items-end justify-between mb-8 border-l-4 border-primary pl-4" style={{ borderColor: 'var(--primary-color)' }}>
                  <h2 className="text-2xl font-bold tracking-tight dark:text-white">{sec.title}</h2>
                  <button
                    onClick={() => handleCategoryClick(sec.key)}
                    className="text-sm font-bold flex items-center gap-1 bg-transparent border-none cursor-pointer"
                    style={{ color: 'var(--primary-strong)' }}
                  >
                    더보기 <span className="material-symbols-outlined text-sm">arrow_forward_ios</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
                  {sec.products.map((product) => (
                    <div key={product.id} onClick={() => handleProductClick(product.id)} className="group flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-[#162a1c] shadow-sm hover:shadow-xl transition-all border border-gray-100 dark:border-gray-800 cursor-pointer">
                      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 dark:bg-gray-800">
                        {product.imageUrl ? (
                          <img
                            className="fade-img h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            src={product.imageUrl}
                            alt={product.name}
                            loading="lazy"
                            onLoad={(e) => e.currentTarget.classList.add('img-loaded')}
                          />
                        ) : (
                          <div className="h-full w-full flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 text-gray-400">
                            <span className="material-symbols-outlined text-4xl mb-1">add_photo_alternate</span>
                            <span className="text-xs">이미지 등록예정</span>
                          </div>
                        )}
                        <button
                          onClick={(e) => handleAddToCart(e, product)}
                          className="absolute bottom-4 right-4 h-8 w-8 lg:h-10 lg:w-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-lg"
                        >
                          <span className="material-symbols-outlined text-background-dark text-lg lg:text-2xl">add_shopping_cart</span>
                        </button>
                      </div>
                      <div className="p-3 lg:p-5">
                        <h4 className="font-bold text-sm lg:text-base line-clamp-1 mb-1 lg:mb-2 text-[#0d1b12] dark:text-white">{product.name}</h4>
                        <div className="flex items-center justify-between">
                          <span className="text-base lg:text-xl font-extrabold text-[#0d1b12] dark:text-white">{formatPrice(product.price)}</span>
                          {product.originalPrice && <span className="text-[10px] lg:text-xs text-gray-400 line-through">{formatPrice(product.originalPrice)}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}

        </main>

        <SellerFooter />

        <FloatingMenu />
      </div>
    </div>
  );
}

export default SellerShop;

